import { useMemo, useState } from 'react';
import menuData from '../asset/menu.json';
import AppHeader from '../components/AppHeader';
import CartBar from '../components/CartBar';
import CartDrawer from '../components/CartDrawer';
import CategorySidebar from '../components/CategorySidebar';
import ProductList from '../components/ProductList';
import './MenuPage.css';

const SHOP = menuData.shop || {};
const CATEGORIES = menuData.categories || [];
const PRODUCTS = menuData.products || [];

function MenuPage() {
  const [activeCategoryId, setActiveCategoryId] = useState(CATEGORIES[0]?.id ?? '');
  const [cartOpen, setCartOpen] = useState(false);
  const [cartById, setCartById] = useState({});

  const productsById = useMemo(() => {
    const map = new Map();

    for (const product of PRODUCTS) {
      map.set(product.id, product);
    }

    return map;
  }, []);

  const activeCategory = useMemo(
    () => CATEGORIES.find((category) => category.id === activeCategoryId) ?? CATEGORIES[0],
    [activeCategoryId]
  );

  const activeProducts = useMemo(
    () => PRODUCTS.filter((product) => product.categoryId === activeCategory?.id),
    [activeCategory]
  );

  const cartItems = useMemo(() => {
    const items = [];

    for (const [productId, qty] of Object.entries(cartById)) {
      if (!qty) {
        continue;
      }

      const product = productsById.get(productId);

      if (!product) {
        continue;
      }

      items.push({ product, qty });
    }

    items.sort((a, b) => a.product.name.localeCompare(b.product.name, 'zh-Hans-CN'));
    return items;
  }, [cartById, productsById]);

  const totalCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.qty, 0),
    [cartItems]
  );
  const totalPrice = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.qty * item.product.price, 0),
    [cartItems]
  );

  function setQty(productId, nextQty) {
    setCartById((prev) => {
      const current = prev[productId] ?? 0;

      if (current === nextQty) {
        return prev;
      }

      const next = { ...prev };

      if (nextQty <= 0) {
        delete next[productId];
      } else {
        next[productId] = nextQty;
      }

      return next;
    });
  }

  function addToCart(productId) {
    setCartById((prev) => ({ ...prev, [productId]: (prev[productId] ?? 0) + 1 }));
  }

  function removeFromCart(productId) {
    setCartById((prev) => {
      const current = prev[productId] ?? 0;

      if (current <= 1) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }

      return { ...prev, [productId]: current - 1 };
    });
  }

  function clearCart() {
    setCartById({});
    setCartOpen(false);
  }

  function toggleCart() {
    if (totalCount <= 0) {
      return;
    }

    setCartOpen((value) => !value);
  }

  return (
    <div className="menuPage">
      <AppHeader level={SHOP.level} title={SHOP.name} />

      <div className="content">
        <CategorySidebar
          categories={CATEGORIES}
          activeCategoryId={activeCategory?.id ?? ''}
          onChange={setActiveCategoryId}
        />
        <ProductList
          title={activeCategory?.name ?? ''}
          products={activeProducts}
          cartById={cartById}
          onAdd={addToCart}
          onRemove={removeFromCart}
        />
      </div>

      <CartDrawer
        open={cartOpen}
        items={cartItems}
        onClose={toggleCart}
        onClear={clearCart}
        onSetQty={setQty}
      />
      <CartBar totalCount={totalCount} totalPrice={totalPrice} onToggleCart={toggleCart} />
    </div>
  );
}

export default MenuPage;
